import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, Clock, User, ChevronLeft, ChevronRight, Trash2, Package } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { useTranslation } from '../../i18n/LocaleContext';

const AdminOrderHistory = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [searchTerm, pagination.page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await adminService.getOrders(searchTerm, 50, pagination.page);
      setOrders(data.orders || []);
      setStats(data.stats || {});
      setPagination(data.pagination || { page: 1, limit: 50, total: 0, total_pages: 0 });
    } catch (error) {
      console.error('Failed to fetch order history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleDeleteUserHistory = async (userId, username) => {
    if (!window.confirm(t('aHistConfirmDeleteUser', { username }))) return;

    try {
      await adminService.deleteUserOrderHistory(userId);
      alert(t('aHistDeletedUser', { username }));
      fetchOrders(); // Reload data
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDeleteAllHistory = async () => {
    if (!window.confirm(t('aHistConfirmDeleteAll'))) return;

    const confirmText = window.prompt(t('aHistConfirmPromptAll'));
    if (confirmText !== 'XOA TAT CA') {
      alert(t('aHistCancelled'));
      return;
    }

    try {
      setLoading(true);
      await adminService.deleteAllOrderHistory();
      alert(t('aHistDeletedAll'));
      fetchOrders(); // Reload data
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    return status === 'completed' ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-900 bg-opacity-30 text-green-400 border border-green-700">
        <CheckCircle className="w-3 h-3" />
        {t('aHistCompleted')}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-900 bg-opacity-30 text-yellow-400 border border-yellow-700">
        <Clock className="w-3 h-3" />
        {t('aHistPending')}
      </span>
    );
  };

  const totalOrders = stats?.total_orders || 0;
  const pendingOrders = stats?.pending_orders || 0;
  const completedOrders = stats?.completed_orders || 0;
  const totalAmount = stats?.total_amount || 0;
  const totalCommission = stats?.total_commission || 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aHistTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aHistSubtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aHistTotalOrders')}</p>
          <p className="text-2xl font-bold text-white">{totalOrders}</p>
        </div>
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aHistPending')}</p>
          <p className="text-2xl font-bold text-white">{pendingOrders}</p>
        </div>
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aHistCompleted')}</p>
          <p className="text-2xl font-bold text-white">{completedOrders}</p>
        </div>
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aHistTotalRevenue')}</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aHistTotalCommission')}</p>
          <p className="text-2xl font-bold text-white">{formatCurrency(totalCommission)}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('aHistSearchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-6 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-500 transition-colors"
          >
            {t('aHistSearch')}
          </button>
          <button
            onClick={handleDeleteAllHistory}
            className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
            title={t('aHistDeleteAllTitle')}
          >
            <Trash2 className="w-5 h-5" />
            {t('aHistDeleteAll')}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColProduct')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColPrice')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColCommission')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">VIP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aHistColActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-400">
                    {t('aHistLoading')}
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-400">
                    {t('aHistEmpty')}
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 text-sm text-gray-300">#{order.id}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{order.username}</p>
                          <p className="text-xs text-gray-400">{order.phone || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {order.product_image && order.product_image.startsWith('http') ? (
                          <img 
                            src={order.product_image} 
                            alt={order.product_name} 
                            className="w-12 h-12 object-cover rounded-lg"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <p className="text-sm text-white">{order.product_name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-white">{formatCurrency(order.price)}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-green-400">{formatCurrency(order.commission)}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-yellow-900 bg-opacity-30 text-yellow-400 rounded text-xs font-medium border border-yellow-700">
                        {order.vip_level || order.vip}
                      </span>
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{new Date(order.created_at).toLocaleString('vi-VN')}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleDeleteUserHistory(order.user_id, order.username)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors"
                        title={t('aHistDeleteUserTitle')}
                      >
                        <Trash2 className="w-3 h-3" />
                        {t('aHistDeleteShort')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700">
            <div className="text-sm text-gray-400">
              {t('aHistShowing', { shown: orders.length, total: pagination.total })}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('aHistPrev')}
              </button>
              <span className="text-sm text-gray-300">
                {t('aHistPageOf', { page: pagination.page, total: pagination.total_pages })}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.total_pages}
                className="px-3 py-1 bg-gray-700 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {t('aHistNext')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrderHistory;
